<template>
  <div v-if="segments.length" class="note-text">
    <template v-for="(seg, i) in segments" :key="i">
      <div v-if="seg.type === 'text'" class="note-text__paragraph">
        <template v-for="(part, j) in splitMentions(seg.text)" :key="j">
          <span v-if="part.mention" class="note-text__mention">{{ part.text }}</span>
          <template v-else>{{ part.text }}</template>
        </template>
      </div>
      <div v-else class="note-text__table-wrap">
        <div
          class="note-text__table-scroll"
          :class="isStacked(seg) ? 'note-text__table-scroll--stacked' : 'note-text__table-scroll--card'"
          @click="onTableTap(seg)">
          <NoteTable
            :rows="seg.rows"
            :rich="seg.type === 'rich'"
            :stacked="isStacked(seg)"
            :fit="smAndDown && !isStacked(seg)" />
        </div>
        <button
          type="button"
          class="note-text__expand"
          aria-label="View table full screen"
          @click.stop="openViewer(seg)">
          <v-icon size="15">mdi-arrow-expand</v-icon>
        </button>
      </div>
    </template>

    <v-dialog
      v-model="viewerOpen"
      :content-class="smAndDown ? 'table-viewer-pop table-viewer-pop--sheet' : 'table-viewer-pop'"
      :transition="smAndDown ? 'dialog-bottom-transition' : 'dialog-transition'">
      <v-card v-if="viewerSeg" class="table-viewer" :class="{ 'table-viewer--sheet': smAndDown }">
        <div v-if="smAndDown" class="table-viewer__handle" />
        <div class="table-viewer__bar">
          <span class="table-viewer__title">Table</span>
          <div class="table-viewer__zoom">
            <v-btn
              icon="mdi-minus"
              variant="text"
              size="small"
              density="comfortable"
              :disabled="zoomIndex === 0"
              @click="zoomIndex--" />
            <span class="table-viewer__zoom-level">{{ ZOOM_STEPS[zoomIndex] }}px</span>
            <v-btn
              icon="mdi-plus"
              variant="text"
              size="small"
              density="comfortable"
              :disabled="zoomIndex === ZOOM_STEPS.length - 1"
              @click="zoomIndex++" />
          </div>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            density="comfortable"
            aria-label="Close table viewer"
            @click="viewerOpen = false" />
        </div>
        <div class="table-viewer__body">
          <NoteTable :rows="viewerSeg.rows" :rich="viewerSeg.type === 'rich'" :font-size="ZOOM_STEPS[zoomIndex]" />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useDisplay } from "vuetify";
import NoteTable from "@/components/NoteTable.vue";
import { parseTableMarker } from "@/utils/pastedTable";

const props = defineProps({
  text: {
    type: String,
    default: "",
  },
});

const ZOOM_STEPS = [12, 14, 16, 18, 21, 24];

const { smAndDown } = useDisplay();

const cellText = (cell) => (cell !== null && typeof cell === "object" ? cell.t : String(cell ?? ""));

// Label/value-dominant tables (BV sheets) read faster as a stacked list on
// phones; genuinely wide grids keep the scrollable table.
function isStacked(seg) {
  if (!smAndDown.value) return false;
  const rows = seg.rows;
  if (rows.length < 3) return false;
  const pairish = rows.filter((row) => row.map(cellText).filter((t) => t).length <= 2).length;
  return pairish / rows.length >= 0.5;
}

const viewerOpen = ref(false);
const viewerSeg = ref(null);
const zoomIndex = ref(2);

function openViewer(seg) {
  viewerSeg.value = seg;
  zoomIndex.value = 2;
  viewerOpen.value = true;
}

function onTableTap(seg) {
  if (window.matchMedia("(pointer: coarse)").matches) openViewer(seg);
}

/**
 * Split a note into renderable segments. Tab-delimited runs (Excel / Google
 * Sheets paste) become tables; [TABLE:{json}] markers become rich tables with
 * per-cell bold/alignment/spans; everything else is whitespace-preserving
 * text. All output is rendered via `{{ }}` interpolation — never v-html — so
 * pasted content cannot inject markup.
 */
function parseSegments(raw) {
  const text = String(raw ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");
  if (!text.trim()) return [];

  const segments = [];
  let textBuf = [];
  let tableBuf = [];

  const flushText = () => {
    if (!textBuf.length) return;
    // Collapse runs of 3+ blank lines and trim surrounding blanks so pasted
    // notes don't render with large vertical gaps.
    const joined = textBuf
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/^\n+|\n+$/g, "");
    if (joined.length) segments.push({ type: "text", text: joined });
    textBuf = [];
  };

  // Excel copies merged cells as a value followed by empty padding cells, so
  // trailing empties are trimmed per row instead of padding rows to a
  // rectangle — short rows end cleanly like the source sheet's merges.
  const flushTable = () => {
    if (!tableBuf.length) return;
    const rows = tableBuf
      .map((line) => {
        const cells = line.split("\t").map((cell) => cell.trim());
        while (cells.length > 1 && !cells[cells.length - 1]) cells.pop();
        return cells;
      })
      .filter((cells) => cells.some((cell) => cell));
    if (rows.length) segments.push({ type: "table", rows });
    tableBuf = [];
  };

  for (const line of text.split("\n")) {
    const richRows = parseTableMarker(line);
    if (richRows) {
      flushText();
      flushTable();
      segments.push({ type: "rich", rows: richRows });
    } else if (line.includes("\t")) {
      flushText();
      tableBuf.push(line);
    } else {
      flushTable();
      textBuf.push(line);
    }
  }
  flushText();
  flushTable();
  return segments;
}

const segments = computed(() => parseSegments(props.text));

const MENTION_REGEX = /(^|\s)(@[A-Z][a-zA-Z]*(?: [A-Z][a-zA-Z]*)?)/g;

function splitMentions(text) {
  const parts = [];
  let last = 0;
  for (const match of text.matchAll(MENTION_REGEX)) {
    const start = match.index + match[1].length;
    if (start > last) parts.push({ mention: false, text: text.slice(last, start) });
    parts.push({ mention: true, text: match[2] });
    last = start + match[2].length;
  }
  if (last < text.length) parts.push({ mention: false, text: text.slice(last) });
  return parts.length ? parts : [{ mention: false, text }];
}
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.note-text {
  min-width: 0;
}

.note-text__paragraph {
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;

  & + .note-text__paragraph {
    margin-top: 6px;
  }
}

.note-text__mention {
  color: $lh_sidebar_accent;
  font-weight: 600;
  background: rgba($lh_sidebar_accent, 0.08);
  border-radius: 4px;
  padding: 0 3px;
}

.note-text__table-wrap {
  position: relative;
  margin: 4px 0;

  & + .note-text__paragraph {
    margin-top: 6px;
  }

  &:hover .note-text__expand,
  &:focus-within .note-text__expand {
    opacity: 1;
  }
}

.note-text__table-scroll--card {
  overflow-x: auto;
  border: 1px solid $lh_dash_border;
  border-radius: 12px;
  padding: 6px 8px;
  background: $lh_dash_surface;
  width: max-content;
  max-width: 100%;
}

.note-text__table-scroll--stacked {
  border: 1px solid $lh_dash_border;
  border-radius: 12px;
  padding: 6px 14px;
  background: $lh_dash_surface;
}

.note-text__expand {
  position: absolute;
  top: 4px;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid $lh_dash_border;
  border-radius: 8px;
  background: rgba(#ffffff, 0.92);
  color: $lh_color_blue_gray;
  cursor: pointer;
  opacity: 0;
  transition: opacity $lh_transition_fast, color $lh_transition_fast;
  z-index: 2;

  &:hover {
    color: $lh_color_navy_dark;
  }

  @media (hover: none) {
    opacity: 1;
  }
}

.table-viewer {
  display: flex;
  flex-direction: column;
  background: $lh_dash_surface;
  border-radius: 16px;
  width: min(1040px, 92vw);
  max-height: 82vh;
  margin: 0 auto;
}

.table-viewer--sheet {
  border-radius: 20px 20px 0 0;
  width: 100vw;
  height: 88vh;
  max-height: 88vh;
}

.table-viewer__handle {
  flex: 0 0 auto;
  width: 40px;
  height: 4px;
  border-radius: 999px;
  background: rgba($lh_color_navy, 0.16);
  margin: 8px auto 0;
}

.table-viewer__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid $lh_dash_border;
  flex: 0 0 auto;
}

.table-viewer__title {
  font-family: $lh_font_family_dm_sans;
  font-size: 15px;
  font-weight: 600;
  color: $lh_color_navy_dark;
}

.table-viewer__zoom {
  display: flex;
  align-items: center;
  gap: 4px;
}

.table-viewer__zoom-level {
  font-family: $lh_font_mono;
  font-size: 12px;
  color: $lh_color_blue_gray;
  min-width: 34px;
  text-align: center;
}

.table-viewer__body {
  flex: 1 1 auto;
  overflow: auto;
  padding: 14px;
  -webkit-overflow-scrolling: touch;
}
</style>

<style lang="scss">
// v-dialog content-class lands on the teleported overlay node, outside this
// component's scope.
.table-viewer-pop {
  margin: auto;
}

.table-viewer-pop--sheet {
  align-self: flex-end;
  margin: auto 0 0 !important;
  width: 100vw;
  max-width: 100vw !important;
  max-height: 88vh !important;

  .table-viewer {
    max-height: 88vh;
  }
}
</style>
